// WebGL Earth for MediaGlobe: a sun-lit textured sphere with drifting clouds, city lights on the
// night side, ocean glare and an atmosphere glow. Returns null when WebGL is not available.
//
// Textures (public/images/globe, equirectangular 2:1): earth-day.jpg is the surface colour;
// earth-data.jpg holds water in red (for the glare) and night lights in green. Both are
// generated from the hand-traced coastlines, and any 2:1 Earth image can replace earth-day.jpg.

const VERTEX = `
attribute vec2 aPos;
void main() {
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec2 uCenter;
uniform float uRadius;
uniform vec2 uSpin;
uniform vec2 uTilt;
uniform vec3 uSun;
uniform float uTime;
uniform float uReady;
uniform sampler2D uDay;
uniform sampler2D uData;

const float PI = 3.14159265;

float hash(vec3 p) {
  p = fract(p * 0.3183099 + 0.1);
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}

float noise(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(mix(hash(i), hash(i + vec3(1.0, 0.0, 0.0)), f.x),
        mix(hash(i + vec3(0.0, 1.0, 0.0)), hash(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
    mix(mix(hash(i + vec3(0.0, 0.0, 1.0)), hash(i + vec3(1.0, 0.0, 1.0)), f.x),
        mix(hash(i + vec3(0.0, 1.0, 1.0)), hash(i + vec3(1.0, 1.0, 1.0)), f.x), f.y),
    f.z);
}

float fbm5(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = p * 2.07 + vec3(17.1, 3.7, 9.2);
    a *= 0.5;
  }
  return v;
}

float fbm3(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * noise(p);
    p = p * 2.11 + vec3(5.3, 11.9, 1.7);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 p = (gl_FragCoord.xy - uCenter) / uRadius;
  float d = length(p);
  if (d > 1.13) {
    gl_FragColor = vec4(0.0);
    return;
  }

  // Thin glow around the edge, brighter towards the sun (kept soft: it sits on a light page).
  float facing = clamp(dot(normalize(p + 1e-5), normalize(uSun.xy + 1e-5)) * 0.5 + 0.5, 0.0, 1.0);
  float haloA = pow(clamp(1.0 - (d - 1.0) / 0.12, 0.0, 1.0), 2.2) * (0.12 + 0.3 * facing);
  vec3 halo = vec3(0.4, 0.64, 1.0);

  if (d >= 1.0) {
    gl_FragColor = vec4(halo * haloA, haloA);
    return;
  }

  // Point on the sphere (view space), turned back into Earth coordinates.
  vec3 n = vec3(p, sqrt(1.0 - d * d));
  float wy = n.y * uTilt.x + n.z * uTilt.y;
  float z1 = -n.y * uTilt.y + n.z * uTilt.x;
  vec3 w = vec3(n.x * uSpin.x - z1 * uSpin.y, wy, n.x * uSpin.y + z1 * uSpin.x);
  vec2 uv = vec2(atan(w.x, w.z) / (2.0 * PI) + 0.5, acos(clamp(w.y, -1.0, 1.0)) / PI);

  vec3 day = pow(texture2D(uDay, uv).rgb, vec3(2.2));
  vec2 data = texture2D(uData, uv).rg;
  day = mix(vec3(0.004, 0.02, 0.06), day, uReady);
  float water = data.r * uReady;
  float lights = data.g * uReady;

  float ndl = dot(n, uSun);
  float lit = smoothstep(-0.14, 0.3, ndl);
  float diffuse = max(ndl, 0.0);

  // Clouds live in Earth space (they turn with it) and drift slowly.
  float lat = asin(clamp(w.y, -1.0, 1.0));
  float belts = 0.55 + 0.3 * exp(-pow((lat - 0.1) / 0.14, 2.0)) + 0.35 * exp(-pow((abs(lat) - 0.95) / 0.3, 2.0))
    - 0.25 * exp(-pow((abs(lat) - 0.42) / 0.14, 2.0));
  vec3 cp = w * 2.2 + vec3(uTime * 0.01, 0.0, uTime * 0.006);
  vec3 warp = vec3(fbm3(cp * 1.4 + 3.1), fbm3(cp * 1.4 + 7.3), fbm3(cp * 1.4 + 1.9));
  float c = fbm5(cp + warp * 1.5);
  float wisps = fbm3(cp * 5.0 + warp * 2.0);
  float mass = smoothstep(0.5 - 0.12 * belts, 0.8 - 0.08 * belts, c) * smoothstep(0.2, 0.62, wisps + 0.12);
  float veil = smoothstep(0.4, 0.72, c) * smoothstep(0.35, 0.75, wisps) * 0.4;
  float cloud = max(mass * 0.95, veil) * uReady;

  vec3 col = day * (0.012 + 1.15 * diffuse);

  vec3 h = normalize(uSun + vec3(0.0, 0.0, 1.0));
  float nh = max(dot(n, h), 0.0);
  float glare = (pow(nh, 140.0) * 0.45 + pow(nh, 22.0) * 0.05) * water;
  col += vec3(1.0, 0.93, 0.8) * glare * lit * (1.0 - cloud);

  // Cloud shadows darken the ground a little, then the clouds themselves.
  col *= 1.0 - 0.25 * cloud;
  col = mix(col, vec3(0.92, 0.95, 1.0) * (0.01 + 1.05 * diffuse), cloud);

  col += vec3(1.0, 0.66, 0.3) * lights * lights * 1.8 * (1.0 - lit) * (1.0 - 0.75 * cloud);

  float rim = pow(1.0 - n.z, 2.2);
  col = mix(col, pow(vec3(0.36, 0.6, 1.0), vec3(2.2)) * (0.1 + 0.9 * lit), rim * 0.75);

  col = pow(col, vec3(1.0 / 2.2));

  float edge = smoothstep(1.0 - 1.6 / uRadius, 1.0, d);
  float a = 1.0 - edge + edge * haloA;
  gl_FragColor = vec4(col * (1.0 - edge) + halo * haloA * edge, a);
}
`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(log);
  }
  return shader;
}

// A canvas has a single WebGL context, so a second renderer on the same canvas (React in
// development mounts, unmounts and mounts again) shares this one. Each picture is therefore
// bound to its own texture unit when it arrives, and a picture that arrives after its renderer
// was thrown away is dropped: it would otherwise land in the other renderer's textures and,
// say, paint the water mask over the Earth's surface.
function loadTexture(gl, url, unit, onLoad, isDisposed) {
  const texture = gl.createTexture();
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    if (isDisposed()) return;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
    // No mipmaps: they would draw a seam where the map wraps round at the date line.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    onLoad();
  };
  image.src = url;
  return { texture, image };
}

// `fade`: fade the surface in once loaded; `onReady`: called when both textures have arrived.
export function createEarthRenderer(canvas, imageBase, { fade = true, onReady } = {}) {
  const gl = canvas.getContext('webgl', { premultipliedAlpha: true, antialias: false });
  if (!gl) return null;

  let program;
  try {
    program = gl.createProgram();
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
  } catch {
    return null;
  }
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const u = {};
  ['uCenter', 'uRadius', 'uSpin', 'uTilt', 'uSun', 'uTime', 'uReady', 'uDay', 'uData'].forEach((name) => {
    u[name] = gl.getUniformLocation(program, name);
  });

  let loaded = 0;
  let readySince = null;
  let disposed = false;
  const isDisposed = () => disposed;
  const onLoad = () => {
    loaded += 1;
    if (loaded === 2) onReady?.();
  };
  const day = loadTexture(gl, `${imageBase}/earth-day.jpg`, 0, onLoad, isDisposed);
  const data = loadTexture(gl, `${imageBase}/earth-data.jpg`, 1, onLoad, isDisposed);
  gl.uniform1i(u.uDay, 0);
  gl.uniform1i(u.uData, 1);

  // Sun from the upper left, slightly in front: most of the globe is in daylight and the
  // night side (with its city lights) curls round the right-hand edge.
  const sun = [-0.62, 0.36, 0.7];
  const len = Math.hypot(...sun);
  gl.uniform3f(u.uSun, sun[0] / len, sun[1] / len, sun[2] / len);

  return {
    // Everything in CSS pixels; `now` in seconds. Returns true while the textures fade in.
    render({ width, height, dpr, cx, cy, radius, spin, tilt, now }) {
      const w = Math.round(width * dpr);
      const h = Math.round(height * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      // Set up from scratch every frame: another renderer may share this canvas's context.
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
      if (loaded === 2 && readySince === null) readySince = now;
      let ready = readySince === null ? 0 : Math.min(1, (now - readySince) / 0.6);
      if (!fade && loaded === 2) ready = 1;
      gl.uniform2f(u.uCenter, cx * dpr, (height - cy) * dpr);
      gl.uniform1f(u.uRadius, radius * dpr);
      gl.uniform2f(u.uSpin, Math.cos(spin), Math.sin(spin));
      gl.uniform2f(u.uTilt, Math.cos(tilt), Math.sin(tilt));
      gl.uniform1f(u.uTime, now);
      gl.uniform1f(u.uReady, ready);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, day.texture);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, data.texture);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      return ready < 1;
    },
    isLost: () => gl.isContextLost(),
    dispose() {
      disposed = true;
      [day, data].forEach((t) => {
        t.image.onload = null;
        gl.deleteTexture(t.texture);
      });
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    },
  };
}
